import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScrollReveal from '@/components/ScrollReveal';
import { ArrowRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const differentiators = [
  'Deep systems engineering and architecture',
  'AI product co-founding and intelligent workflow design',
  'Enterprise go-to-market and revenue leadership',
  'Executive-level advisory and board decision systems',
];

export default function TeamPage() {
  useEffect(() => {
    ScrollTrigger.refresh();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="pt-44 pb-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
          <div className="container-custom relative z-10">
            <ScrollReveal className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                Founding Team
              </h1>
              <p className="text-lg sm:text-xl text-slate-600">
                Built by Leaders with Over 60 Years of Enterprise Technology
                Experience
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Team Introduction */}
        <section className="py-10 md:py-16">
          <div className="container-custom">
            <ScrollReveal>
              <div className="max-w-4xl mx-auto">
                <p className="text-xl text-slate-600 leading-relaxed mb-8">
                  CoDRIVER LABS was founded by a team of senior technology leaders,
                  product builders and enterprise executives with more than{' '}
                  <span className="text-slate-900 font-semibold">
                    60 years of combined experience
                  </span>{' '}
                  delivering mission-critical systems across AI, cloud, telecom,
                  fintech, pharma and government.
                </p>

                <p className="text-slate-600 leading-relaxed mb-8">
                  Our founders have led engineering, product and commercial
                  organisations at some of the world's leading technology
                  companies, building platforms used by Tier-1 operators, regulated
                  financial institutions, pharmaceutical companies and national
                  governments across Europe.
                </p>

                <div className="bg-slate-50 rounded-xl p-8 border border-slate-200 mb-8">
                  <h3 className="text-slate-900 font-semibold mb-4">
                    What sets the CoDRIVER LABS founding team apart:
                  </h3>
                  <ul className="space-y-3">
                    {differentiators.map((item, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 text-slate-600"
                      >
                        <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-lg text-slate-900 font-medium">
                  We are not advisors experimenting with AI — we are operators who
                  have built, deployed and scaled complex platforms in production
                  for decades.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Philosophy */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal>
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">
                  Our Philosophy
                </h2>
                <p className="text-slate-600 mb-6">
                  At CoDRIVER LABS, our founding team operates with a simple
                  principle:
                </p>
                <blockquote className="text-xl sm:text-2xl text-slate-900 font-medium italic mb-8 border-l-4 border-primary pl-6 text-left">
                  "Build AI the way enterprise systems should be built — secure,
                  scalable, compliant and commercially grounded."
                </blockquote>
                <p className="text-slate-600">
                  With over 60 years of combined founder experience, we bring
                  execution depth across product, engineering, commercial strategy
                  and executive advisory — enabling us to deliver AI systems that
                  move beyond experimentation and into real business impact.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <ScrollReveal className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Work with founders who understand production AI
              </h2>
              <p className="text-slate-600 mb-8">
                Book a discovery call to discuss your AI strategy with our founding
                team.
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2 group"
              >
                Book a Discovery Call
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
